import React, { createContext, useState, useEffect } from "react";
import api from "../services/api";
import voImg from "../assets/quà tặng/vớ.png";
import chaiImg from "../assets/quà tặng/chai khử mùi.jpg";

const GIFTS = [
  {
    productId: "gift_socks",
    name: "Quà tặng: Vớ thể thao cao cấp",
    image: voImg,
    price: 0,
    originalPrice: 39000,
    selectedColor: "Trắng",
    selectedSize: "Freesize",
    isGift: true,
  },
  {
    productId: "gift_deodorant",
    name: "Quà tặng: Chai khử mùi giày",
    image: chaiImg,
    price: 0,
    originalPrice: 59000,
    selectedColor: "Mặc định",
    selectedSize: "260ml",
    isGift: true,
  },
];

const LOCAL_KEY = "tahashoes_cart";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);

  const isLoggedIn = () => {
    const token = localStorage.getItem("token");
    return token && token !== "undefined" && token !== "null";
  };

  const getLocalCart = () => {
    const data = localStorage.getItem(LOCAL_KEY);
    try {
      const parsed = JSON.parse(data);
      return parsed && Array.isArray(parsed.items) ? parsed : { items: [] };
    } catch {
      return { items: [] };
    }
  };

  const saveLocal = (newCart) => {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(newCart));
    setCart(newCart);
  };

  const syncCartToServer = async () => {
    try {
      const localCart = getLocalCart();

      if (!localCart.items || localCart.items.length === 0) {
        console.log("📦 No items in localStorage to sync");
        return;
      }

      console.log("🔄 Syncing", localCart.items.length, "items to server...");

      for (const item of localCart.items) {
        try {
          await api.post("/cart", {
            productId: item.productId,
            qty: item.qty,
            selectedColor: item.selectedColor || "Mặc định",
            selectedSize: item.selectedSize || "One Size",
            price: item.price,
            name: item.name,
            image: item.image,
          });
          console.log("✅ Synced item:", item.name);
        } catch (err) {
          console.error("⚠️ Failed to sync item:", item.name, err.message);
        }
      }

      localStorage.removeItem(LOCAL_KEY);
      console.log("✅ Cart synced and localStorage cleared");

      const res = await api.get("/cart");
      setCart(res.data);
      console.log("✅ Cart loaded from server:", res.data);
    } catch (err) {
      console.error("❌ Sync cart failed:", err.message);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (isLoggedIn()) {
        try {
          setLoading(true);
          const res = await api.get("/cart");
          console.log("✅ Cart from server:", res.data);

          if (res.data && res.data.items) {
            setCart(res.data);
          } else {
            setCart({ items: [] });
          }
        } catch (err) {
          console.warn("⚠️ Fetch cart from server failed:", err.message);
          const local = getLocalCart();
          setCart(local);
        } finally {
          setLoading(false);
        }
      } else {
        const local = getLocalCart();
        console.log("📦 Cart from localStorage:", local);
        setCart(local);
      }
    };

    init();
  }, []);

  const addToCart = async (payload) => {
    const {
      productId,
      qty,
      selectedColor,
      selectedSize,
      price,
      name,
      image,
      quantity,
      _id,
    } = payload;

    const actualQty = qty || quantity || 1;
    const actualProductId = productId || _id;
    const actualColor = selectedColor || "Mặc định";
    const actualSize = selectedSize || "One Size";

    console.log("🛒 Adding to cart:", {
      actualProductId,
      actualQty,
      actualColor,
      actualSize,
    });

    if (isLoggedIn()) {
      try {
        const res = await api.post("/cart", {
          productId: actualProductId,
          qty: actualQty,
          selectedColor: actualColor,
          selectedSize: actualSize,
          price: price || 0,
          name: name || "Sản phẩm",
          image: image || "",
        });

        // Tự động thêm quà tặng lên server
        for (const gift of GIFTS) {
          await api.post("/cart", {
            ...gift,
            qty: actualQty,
            isGift: true, // Đảm bảo backend nhận diện là quà
            originalPrice: gift.originalPrice, // Lưu giá gốc
          });
        }

        const finalRes = await api.get("/cart");
        setCart(finalRes.data);
        return finalRes.data;
      } catch (err) {
        console.error("❌ Add to cart API failed:", err.message);
        return addToLocalCart(payload);
      }
    } else {
      return addToLocalCart(payload);
    }
  };

  const addToLocalCart = (payload) => {
    const {
      productId,
      qty,
      selectedColor,
      selectedSize,
      price,
      name,
      image,
      quantity,
      _id,
    } = payload;

    const actualQty = qty || quantity || 1;
    const actualProductId = productId || _id;
    const actualColor = selectedColor || "Mặc định";
    const actualSize = selectedSize || "One Size";

    const currentCart = getLocalCart();

    if (!Array.isArray(currentCart.items)) {
      currentCart.items = [];
    }

    const existingIndex = currentCart.items.findIndex(
      (i) =>
        i.productId === actualProductId &&
        i.selectedColor === actualColor &&
        i.selectedSize === actualSize,
    );

    if (existingIndex > -1) {
      currentCart.items[existingIndex].qty += actualQty;
      console.log("📝 Updated existing item in localStorage");
    } else {
      currentCart.items.push({
        productId: actualProductId,
        qty: actualQty,
        selectedColor: actualColor,
        selectedSize: actualSize,
        price: price || 0,
        name: name || "Sản phẩm",
        image: image || "",
      });
      console.log("➕ Added new item to localStorage");
    }

    // Tự động thêm quà vào localStorage
    GIFTS.forEach((gift) => {
      const gIndex = currentCart.items.findIndex(
        (i) => i.productId === gift.productId,
      );
      if (gIndex > -1) {
        currentCart.items[gIndex].qty += actualQty;
      } else {
        currentCart.items.push({
          ...gift,
          qty: actualQty,
        });
      }
    });

    saveLocal(currentCart);
    return currentCart;
  };

  const updateQuantity = async (
    productId,
    selectedColor,
    selectedSize,
    newQty,
  ) => {
    console.log("🔄 Updating quantity:", {
      productId,
      selectedColor,
      selectedSize,
      newQty,
    });

    if (isLoggedIn()) {
      try {
        const res = await api.put("/cart/update", {
          productId,
          selectedColor,
          selectedSize,
          qty: newQty,
        });

        // Nếu không phải là quà, cập nhật lại số lượng quà tương ứng
        const isGift = GIFTS.some((g) => g.productId === productId);
        if (!isGift) {
          // Tính tổng số lượng sản phẩm không phải quà
          const totalNormalQty = res.data.items
            .filter((i) => !GIFTS.some((g) => g.productId === i.productId))
            .reduce((sum, i) => sum + i.qty, 0);

          for (const gift of GIFTS) {
            await api.put("/cart/update", {
              productId: gift.productId,
              selectedColor: gift.selectedColor,
              selectedSize: gift.selectedSize,
              qty: totalNormalQty,
            });
          }
        }

        const finalRes = await api.get("/cart");
        setCart(finalRes.data);
        return finalRes.data;
      } catch (err) {
        console.error("❌ Update quantity API failed:", err.message);
        updateLocalQuantity(productId, selectedColor, selectedSize, newQty);
      }
    } else {
      updateLocalQuantity(productId, selectedColor, selectedSize, newQty);
    }
  };

  const updateLocalQuantity = (
    productId,
    selectedColor,
    selectedSize,
    newQty,
  ) => {
    const currentCart = getLocalCart();

    if (newQty < 1) {
      currentCart.items = currentCart.items.filter(
        (i) =>
          !(
            i.productId === productId &&
            i.selectedColor === selectedColor &&
            i.selectedSize === selectedSize
          ),
      );
    } else {
      const itemIndex = currentCart.items.findIndex(
        (i) =>
          i.productId === productId &&
          i.selectedColor === selectedColor &&
          i.selectedSize === selectedSize,
      );

      if (itemIndex > -1) {
        currentCart.items[itemIndex].qty = newQty;
      }
    }

    // Đồng bộ quà tặng trong localStorage
    const normalItems = currentCart.items.filter(
      (i) => !GIFTS.some((g) => g.productId === i.productId),
    );
    const totalNormalQty = normalItems.reduce((sum, i) => sum + i.qty, 0);

    if (totalNormalQty === 0) {
      // Nếu không còn sản phẩm chính nào, xóa hết quà
      currentCart.items = currentCart.items.filter(
        (i) => !GIFTS.some((g) => g.productId === i.productId),
      );
    } else {
      GIFTS.forEach((gift) => {
        const gIndex = currentCart.items.findIndex(
          (i) => i.productId === gift.productId,
        );
        if (gIndex > -1) {
          currentCart.items[gIndex].qty = totalNormalQty;
        } else {
          currentCart.items.push({ ...gift, qty: totalNormalQty });
        }
      });
    }

    saveLocal(currentCart);
    return currentCart;
  };

  const removeItem = async (productId, selectedColor, selectedSize) => {
    console.log("🗑️ Removing item:", {
      productId,
      selectedColor,
      selectedSize,
    });

    if (isLoggedIn()) {
      try {
        const res = await api.delete("/cart/remove", {
          data: {
            productId,
            selectedColor,
            selectedSize,
          },
        });

        // Nếu không phải xóa quà, cập nhật lại số lượng quà
        const isGift = GIFTS.some((g) => g.productId === productId);
        if (!isGift) {
          const totalNormalQty = res.data.items
            .filter((i) => !GIFTS.some((g) => g.productId === i.productId))
            .reduce((sum, i) => sum + i.qty, 0);

          if (totalNormalQty === 0) {
            // Xóa toàn bộ quà nếu không còn hàng chính
            for (const gift of GIFTS) {
              await api.delete("/cart/remove", {
                data: {
                  productId: gift.productId,
                  selectedColor: gift.selectedColor,
                  selectedSize: gift.selectedSize,
                },
              });
            }
          } else {
            // Cập nhật số lượng quà bằng tổng hàng chính còn lại
            for (const gift of GIFTS) {
              await api.put("/cart/update", {
                productId: gift.productId,
                selectedColor: gift.selectedColor,
                selectedSize: gift.selectedSize,
                qty: totalNormalQty,
              });
            }
          }
        }

        const finalRes = await api.get("/cart");
        setCart(finalRes.data);
        return finalRes.data;
      } catch (err) {
        console.error("❌ Remove item API failed:", err.message);
        removeLocalItem(productId, selectedColor, selectedSize);
      }
    } else {
      removeLocalItem(productId, selectedColor, selectedSize);
    }
  };

  const removeLocalItem = (productId, selectedColor, selectedSize) => {
    const currentCart = getLocalCart();
    currentCart.items = currentCart.items.filter(
      (i) =>
        !(
          i.productId === productId &&
          i.selectedColor === selectedColor &&
          i.selectedSize === selectedSize
        ),
    );

    // Đồng bộ quà tặng trong localStorage
    const normalItems = currentCart.items.filter(
      (i) => !GIFTS.some((g) => g.productId === i.productId),
    );
    const totalNormalQty = normalItems.reduce((sum, i) => sum + i.qty, 0);

    if (totalNormalQty === 0) {
      currentCart.items = currentCart.items.filter(
        (i) => !GIFTS.some((g) => g.productId === i.productId),
      );
    } else {
      GIFTS.forEach((gift) => {
        const gIndex = currentCart.items.findIndex(
          (i) => i.productId === gift.productId,
        );
        if (gIndex > -1) {
          currentCart.items[gIndex].qty = totalNormalQty;
        }
      });
    }

    saveLocal(currentCart);
    return currentCart;
  };

  const clearCart = async () => {
    console.log("🗑️ Clearing cart");

    if (isLoggedIn()) {
      try {
        await api.delete("/cart/clear");
        console.log("✅ Cleared server cart");
      } catch (err) {
        console.error("❌ Clear cart API failed:", err.message);
      }
    }

    saveLocal({ items: [] });
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeItem,
        setCart,
        clearCart,
        syncCartToServer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
